import { useState, useEffect, useCallback, useRef } from 'react';
import { OnboardingResponse, OnboardingPrerequisites, DEFAULT_ONBOARDING_RESPONSE } from '@optidev/shared-types';
import { onboardingApi, onboardingEvents } from '@/lib/api/onboarding';
import { analytics } from '@lib/analytics';
import { firebaseAuth } from '@/lib/firebase';
import { useAuthToken } from './useAuthToken';

/**
 * Activation marker tracking. Headless since the onboarding checklist it used to
 * drive was replaced by the editor tour (see components/onboarding/editor-tour).
 *
 * It stays because the markers are what the activation funnels read:
 * `first_project_created` and `first_project_published` are fired from
 * markStepIfNeeded and carry Google Ads conversion values, `first_project_edit`
 * is fired from here, and the GET backfills timestamps for users whose mark
 * calls never landed.
 */

/** Build user-scoped localStorage key */
function getUserKey(suffix: string): string | null {
  const uid = firebaseAuth.currentUser?.uid;
  return uid ? `onb:${uid}:${suffix}` : null;
}

/**
 * Skip the fetch when localStorage already says all three markers are set.
 * Cheap win for returning users, and safe because a false negative only costs
 * one request.
 */
function isOnboardingDoneLocally(): boolean {
  const uid = firebaseAuth.currentUser?.uid;
  if (!uid) return false;

  const step1 = localStorage.getItem(`onb:${uid}:step1_marked`) === 'true';
  const step2 = localStorage.getItem(`onb:${uid}:step2_marked`) === 'true';
  const step3 = localStorage.getItem(`onb:${uid}:step3_marked`) === 'true';

  return step1 && step2 && step3;
}

export interface UseOnboardingReturn {
  prerequisites: OnboardingPrerequisites;
  isLoading: boolean;
  markFirstEditIfNeeded: () => void;
}

export function useOnboarding(): UseOnboardingReturn {
  const { token, loading: tokenLoading } = useAuthToken();
  const [data, setData] = useState<OnboardingResponse>(DEFAULT_ONBOARDING_RESPONSE);
  const [loading, setLoading] = useState(true);
  const calledThisSession = useRef(false);

  const fetchProgress = useCallback(async () => {
    if (!token) return;

    try {
      const response = await onboardingApi.getProgress(token);
      setData(response);
    } catch (err) {
      console.error('Failed to fetch onboarding progress:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!tokenLoading && token) {
      if (isOnboardingDoneLocally()) {
        setLoading(false);
        return;
      }
      setLoading(true);
      fetchProgress();
    } else if (!tokenLoading && !token) {
      setLoading(false);
    }
  }, [tokenLoading, token]);

  // Refetch after a step is marked elsewhere (markStepIfNeeded lives outside React).
  useEffect(() => {
    return onboardingEvents.subscribe(() => fetchProgress());
  }, [fetchProgress]);

  const markFirstEditIfNeeded = useCallback(() => {
    // Gate on the marker itself, not on any UI being visible. The previous
    // version also required the checklist to be on screen, which meant the
    // `first_project_edit` event silently stopped firing for anyone who had
    // dismissed it.
    if (data.prerequisites.step2Completed) return;
    if (calledThisSession.current) return;

    const step2Key = getUserKey('step2_marked');
    if (!step2Key) return; // No UID yet
    // Server is the source of truth: if it says step 2 is not complete, a local
    // flag saying otherwise is stale (a DB reset, or a mark that never landed).
    if (localStorage.getItem(step2Key) === 'true') {
      localStorage.removeItem(step2Key);
    }

    if (!token) return;

    calledThisSession.current = true;

    onboardingApi.markFirstEdit(token)
      .then((response) => {
        localStorage.setItem(step2Key, 'true');
        onboardingEvents._emit();
        // Only on the backend's first confirmation, so the event is not
        // duplicated across browsers or devices.
        if (response.marked) {
          analytics.track('first_project_edit');
        }
      })
      .catch(() => {
        // Allow a retry on the next edit action.
        calledThisSession.current = false;
      });
  }, [data.prerequisites.step2Completed, token]);

  return {
    prerequisites: data.prerequisites,
    isLoading: loading || tokenLoading,
    markFirstEditIfNeeded,
  };
}
